import { Alert, Input, cn } from '@blink/ui';
import { Eye, EyeOff } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useI18n } from '@/i18n';
import { ApiClientError } from '@/lib/api';

/** Turns an API error into a sentence for the courier. */
export function useErrorText() {
  const { t, f, formatTime } = useI18n();
  return (err: unknown): string => {
    if (!(err instanceof ApiClientError)) return t.errors.INTERNAL;
    const details = err.details as { problems?: string[]; minLength?: number; lockedUntil?: string } | undefined;
    if (err.code === 'PASSWORD_POLICY' && details?.problems?.length) {
      return details.problems
        .map((p) => f(t.passwordProblems[p as keyof typeof t.passwordProblems] ?? t.errors.PASSWORD_POLICY, { min: details.minLength ?? 12 }))
        .join(' ');
    }
    if (err.code === 'ACCOUNT_LOCKED' && details?.lockedUntil) return f(t.login.lockedUntil, { time: formatTime(details.lockedUntil) });
    return t.errors[err.code as keyof typeof t.errors] ?? t.errors.INTERNAL;
  };
}

export function ErrorAlert({ error }: { error: unknown }) {
  const text = useErrorText();
  if (!error) return null;
  return (
    <Alert variant="destructive" role="alert">
      {text(error)}
    </Alert>
  );
}

export function PasswordInput(props: React.InputHTMLAttributes<HTMLInputElement>) {
  const { t } = useI18n();
  const [shown, setShown] = useState(false);
  return (
    <div className="relative">
      <Input {...props} type={shown ? 'text' : 'password'} className={cn('h-12 pe-12 text-base', props.className)} />
      <button
        type="button"
        className="absolute inset-y-0 end-0 flex w-12 items-center justify-center text-muted-foreground"
        aria-label={shown ? t.login.hidePassword : t.login.showPassword}
        onClick={() => setShown((s) => !s)}
      >
        {shown ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
      </button>
    </div>
  );
}

/** Phone-width page with the app's navy header band. */
export function Screen({ title, actions, children }: { title: ReactNode; actions?: ReactNode; children: ReactNode }) {
  return (
    <div className="mx-auto flex min-h-dvh w-full max-w-md flex-col">
      <header className="flex items-center gap-3 bg-sidebar px-4 pb-4 pt-[max(1rem,env(safe-area-inset-top))] text-white">
        <div className="min-w-0 flex-1">{title}</div>
        {actions}
      </header>
      <main className="flex-1 space-y-4 p-4">{children}</main>
    </div>
  );
}
